import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal, flushSync } from "react-dom";
import { ChevronDown, Search, X } from "lucide-react";
import {
  AsYouType,
  getCountries,
  getCountryCallingCode,
  type CountryCode,
} from "libphonenumber-js";

const COUNTRY_NAMES: Partial<Record<CountryCode, string>> = {
  AE: "United Arab Emirates",
  IL: "Israel",
  US: "United States",
  GB: "United Kingdom",
  IN: "India",
  EG: "Egypt",
  PK: "Pakistan",
  PH: "Philippines",
  ZA: "South Africa",
  TR: "Turkey",
  SA: "Saudi Arabia",
  JO: "Jordan",
  LB: "Lebanon",
  KW: "Kuwait",
  QA: "Qatar",
  BH: "Bahrain",
  OM: "Oman",
  IQ: "Iraq",
  SY: "Syria",
  YE: "Yemen",
  MA: "Morocco",
  TN: "Tunisia",
  DZ: "Algeria",
  LY: "Libya",
  SD: "Sudan",
  KE: "Kenya",
  NG: "Nigeria",
  FR: "France",
  DE: "Germany",
  IT: "Italy",
  ES: "Spain",
  CA: "Canada",
  AU: "Australia",
  NZ: "New Zealand",
  CN: "China",
  JP: "Japan",
  KR: "South Korea",
  RU: "Russia",
  UA: "Ukraine",
  BR: "Brazil",
  MX: "Mexico",
  AR: "Argentina",
  CH: "Switzerland",
  NL: "Netherlands",
  BE: "Belgium",
  SE: "Sweden",
  NO: "Norway",
  DK: "Denmark",
  PL: "Poland",
  CZ: "Czechia",
  GR: "Greece",
  PT: "Portugal",
  IE: "Ireland",
  AT: "Austria",
  FI: "Finland",
  HU: "Hungary",
  RO: "Romania",
  TH: "Thailand",
  VN: "Vietnam",
  ID: "Indonesia",
  MY: "Malaysia",
  SG: "Singapore",
};

const PINNED: CountryCode[] = [
  "AE", "IL", "SA", "EG", "JO", "LB", "KW", "QA", "BH", "OM",
  "TR", "IN", "PK", "PH", "ZA", "GB", "US",
];

function flagEmoji(code: string): string {
  return code
    .toUpperCase()
    .split("")
    .map((c) => String.fromCodePoint(0x1f1e6 - 0x41 + c.charCodeAt(0)))
    .join("");
}

interface Props {
  country: CountryCode;
  national: string;
  onCountryChange: (c: CountryCode) => void;
  onNationalChange: (v: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
}

export default function CountryPhoneInput({
  country,
  national,
  onCountryChange,
  onNationalChange,
  disabled,
  autoFocus,
}: Props) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const nationalRef = useRef<HTMLInputElement>(null);
  const filterRef = useRef<HTMLInputElement>(null);

  // When the picker opens, focus the search field. When it closes, kick
  // focus back to the national-number input so the user keeps typing.
  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => filterRef.current?.focus());
      // Lock body scroll while sheet is up.
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);

  // ESC closes the sheet.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setOpen(false);
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const countries = useMemo<CountryCode[]>(() => {
    const all = getCountries();
    const seen = new Set<CountryCode>();
    const ordered: CountryCode[] = [];
    for (const p of PINNED) {
      if (all.includes(p)) {
        ordered.push(p);
        seen.add(p);
      }
    }
    for (const c of all) {
      if (!seen.has(c)) ordered.push(c);
    }
    return ordered;
  }, []);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return countries;
    return countries.filter((c) => {
      const name = COUNTRY_NAMES[c] ?? c;
      const code = `+${getCountryCallingCode(c)}`;
      return (
        c.toLowerCase().includes(q) ||
        name.toLowerCase().includes(q) ||
        code.includes(q)
      );
    });
  }, [filter, countries]);

  const dialCode = `+${getCountryCallingCode(country)}`;
  const formatted = new AsYouType(country).input(national);

  function handleNationalInput(raw: string) {
    onNationalChange(raw.replace(/[^\d]/g, ""));
  }

  function pickCountry(c: CountryCode) {
    // iOS Safari only allows programmatic focus to bring up the keyboard
    // when the call happens *synchronously* inside the same user gesture
    // (touchend / click). React batches state updates, which would push
    // the unmount of the modal out of the gesture's call stack and the
    // focus would silently be ignored. `flushSync` forces React to
    // commit the close+update synchronously so the input becomes the
    // topmost focusable element *before* the gesture ends, and the
    // subsequent .focus() reliably opens the number-pad keyboard.
    flushSync(() => {
      onCountryChange(c);
      setFilter("");
      setOpen(false);
    });
    nationalRef.current?.focus();
  }

  return (
    <>
      <div className="flex items-stretch rounded-lg bg-slate-900/80 ring-1 ring-slate-700 focus-within:ring-sky-500">
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={open}
          className="flex items-center gap-1.5 px-2.5 py-2 border-r border-slate-700/80 hover:bg-slate-800/60 rounded-l-lg disabled:opacity-50"
        >
          <span className="text-lg leading-none">{flagEmoji(country)}</span>
          <span className="text-slate-200 text-sm tabular-nums">{dialCode}</span>
          <ChevronDown size={14} className="text-slate-500" />
        </button>
        <input
          ref={nationalRef}
          value={formatted}
          onChange={(e) => handleNationalInput(e.target.value)}
          inputMode="tel"
          autoComplete="tel-national"
          autoFocus={autoFocus}
          disabled={disabled}
          placeholder="50 123 4567"
          className="flex-1 bg-transparent px-3 py-2 text-slate-100 outline-none"
        />
      </div>

      {open && createPortal(<CountryPickerSheet
        country={country}
        filter={filter}
        filterRef={filterRef}
        filtered={filtered}
        onFilterChange={setFilter}
        onPick={pickCountry}
        onClose={() => setOpen(false)}
      />, document.body)}
    </>
  );
}

function CountryPickerSheet({
  country,
  filter,
  filterRef,
  filtered,
  onFilterChange,
  onPick,
  onClose,
}: {
  country: CountryCode;
  filter: string;
  filterRef: React.RefObject<HTMLInputElement | null>;
  filtered: CountryCode[];
  onFilterChange: (v: string) => void;
  onPick: (c: CountryCode) => void;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-[1200] flex items-end sm:items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label="Select country code"
    >
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className={
          "relative w-full sm:max-w-md max-h-[85vh] flex flex-col " +
          "bg-[var(--color-surface)] rounded-t-2xl sm:rounded-2xl " +
          "ring-1 ring-[var(--color-border)] shadow-2xl"
        }
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
          <span className="text-base font-semibold text-slate-100">
            Select country code
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1.5 rounded-md text-slate-400 hover:text-slate-100 hover:bg-slate-800"
          >
            <X size={18} />
          </button>
        </div>
        <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-800">
          <Search size={16} className="text-slate-500 shrink-0" />
          <input
            ref={filterRef}
            value={filter}
            onChange={(e) => onFilterChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && filtered[0]) {
                e.preventDefault();
                onPick(filtered[0]);
              }
            }}
            placeholder="Search country or +code"
            className="bg-transparent flex-1 text-sm text-slate-100 outline-none py-1"
          />
        </div>
        <ul className="flex-1 overflow-y-auto py-1">
          {filtered.length === 0 && (
            <li className="px-4 py-3 text-sm text-slate-500">No matches.</li>
          )}
          {filtered.map((c) => {
            const name = COUNTRY_NAMES[c] ?? c;
            const code = `+${getCountryCallingCode(c)}`;
            const active = c === country;
            return (
              <li key={c}>
                <button
                  type="button"
                  onClick={() => onPick(c)}
                  className={
                    "w-full flex items-center gap-3 px-4 py-3 text-sm " +
                    "active:bg-slate-700 hover:bg-slate-800 " +
                    (active ? "bg-slate-800/60 text-sky-300" : "text-slate-200")
                  }
                >
                  <span className="text-xl leading-none">{flagEmoji(c)}</span>
                  <span className="flex-1 text-left truncate">{name}</span>
                  <span className="text-slate-400 tabular-nums">{code}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
